import { Injectable } from '@angular/core';
import { BaseNotifier } from '../../../../shared/notifiers/base-notifier';
import { Ref } from '../../../../shared/providers/ref';
import { HelpGuideState, HelpGuideStep } from './help-guide-state';

const STORAGE_KEY = 'ghp_guide_seen';

const DEFAULT_STEPS: HelpGuideStep[] = [
  {
    title: 'Ámbito Territorial',
    subtitle: 'Filtro por Departamento, Provincia y Distrito',
    badge: 'Paso 1 · Ubicación',
    iconName: 'map-pin',
    description:
      'En la barra superior puedes consultar la situación agroclimática a nivel Nacional o filtrar de forma progresiva por Departamento, Provincia y Distrito.',
    tips: [
      'Al cambiar de nivel, el visor reenfoca el mapa y recalcula las estadísticas al instante.',
      'Puedes usar el selector de departamento para regresar a la vista Nacional en cualquier momento.',
    ],
  },
  {
    title: 'Mapa Interactivo',
    subtitle: 'Navegación espacial y herramientas cartográficas',
    badge: 'Paso 2 · Visor',
    iconName: 'map',
    description:
      'El mapa es el centro de la toma de decisiones. Dispone de navegación fluida, capas temáticas y herramientas espaciales.',
    tips: [
      'Usa los controles de la barra derecha para acercar, alejar o restablecer la vista a nivel Nacional (↺).',
      'Accede a la medición de distancias/áreas y catálogo de capas desde la barra lateral izquierda.',
    ],
  },
  {
    title: 'Cifras Clave Ejecutivas',
    subtitle: 'Superficie expuesta y productores agrícolas en riesgo',
    badge: 'Paso 3 · Impacto',
    iconName: 'chart-bar',
    description:
      'La franja superior de estadísticas muestra el consolidado de hectáreas agrícolas, productores y cultivos expuestos según el nivel de severidad meteorológica.',
    tips: [
      'Haz clic en la pestaña "Cifras clave" para expandir o colapsar la banda y priorizar el mapa.',
      'Toca cualquiera de las tarjetas para desglosar el detalle de cultivos y zonas comprometidas.',
    ],
  },
  {
    title: 'Centro de Decisión y Alertas',
    subtitle: 'Avisos oficiales SENAMHI y pronóstico Nowcasting',
    badge: 'Paso 4 · Decisión',
    iconName: 'alert-triangle',
    description:
      'El panel de decisión organiza los avisos meteorológicos vigentes y pronósticos de precipitación a corto plazo para anticipar acciones de prevención.',
    tips: [
      'En pantallas de escritorio se ubica a la derecha; en móviles y tablets se presenta como panel inferior colapsable.',
      'La leyenda interactiva clasifica los niveles de severidad: Amarillo (Moderado), Naranja (Fuerte) y Rojo (Muy Fuerte).',
    ],
  },
];

@Injectable()
export class HelpGuideNotifier extends BaseNotifier<HelpGuideState> {
  private static readonly INITIAL: HelpGuideState = {
    visible: false,
    currentStep: 0,
    steps: DEFAULT_STEPS,
    autoOpened: false,
  };

  constructor(ref: Ref) {
    super(ref, HelpGuideNotifier.INITIAL);
  }

  override build(): void {
    const seen = localStorage.getItem(STORAGE_KEY) === 'true';
    if (!seen) {
      // Abre automáticamente la primera vez con un ligero delay para que el visor cargue primero
      window.setTimeout(() => {
        if (!this.snapshot().visible) {
          this.updateState((s) => ({
            ...s,
            visible: true,
            currentStep: 0,
            autoOpened: true,
          }));
        }
      }, 900);
    }
  }

  open(step = 0): void {
    const s = this.snapshot();
    const clampedStep = Math.max(0, Math.min(step, s.steps.length - 1));
    this.updateState((state) => ({
      ...state,
      visible: true,
      currentStep: clampedStep,
    }));
  }

  close(): void {
    this.markAsSeen();
    this.updateState((s) => ({ ...s, visible: false }));
  }

  next(): void {
    const s = this.snapshot();
    if (s.currentStep < s.steps.length - 1) {
      this.updateState((state) => ({ ...state, currentStep: state.currentStep + 1 }));
    } else {
      this.close();
    }
  }

  prev(): void {
    const s = this.snapshot();
    if (s.currentStep > 0) {
      this.updateState((state) => ({ ...state, currentStep: state.currentStep - 1 }));
    }
  }

  setStep(stepIndex: number): void {
    const s = this.snapshot();
    if (stepIndex >= 0 && stepIndex < s.steps.length) {
      this.updateState((state) => ({ ...state, currentStep: stepIndex }));
    }
  }

  private markAsSeen(): void {
    try {
      localStorage.setItem(STORAGE_KEY, 'true');
    } catch {
      // Ignorar restricciones de almacenamiento local en navegación privada
    }
  }
}
